import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS_EN = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const DAY_LABELS_ID = { mon: "Sen", tue: "Sel", wed: "Rab", thu: "Kam", fri: "Jum", sat: "Sab", sun: "Min" };

/** Compute local now in the target IANA timezone, return {dayKey, minutes}. */
function nowInTimezone(tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || "Asia/Brunei",
      weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(new Date());
    const wd = parts.find((p) => p.type === "weekday")?.value?.toLowerCase() || "mon";
    const hh = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
    const mm = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
    const map = { sun: "sun", mon: "mon", tue: "tue", wed: "wed", thu: "thu", fri: "fri", sat: "sat" };
    return { dayKey: map[wd] || "mon", minutes: hh * 60 + mm };
  } catch {
    const d = new Date();
    return { dayKey: DAY_KEYS[d.getDay()], minutes: d.getHours() * 60 + d.getMinutes() };
  }
}

function toMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function fmtHHMM(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Compute status. Returns:
 *  { state: "open" | "closed_soon" | "closed", label, sub, next }
 */
function computeStatus(hours, lang = "en") {
  if (!hours || !hours.days) return null;
  const { dayKey, minutes } = nowInTimezone(hours.timezone);
  const today = hours.days[dayKey];
  const labels = lang === "id" ? DAY_LABELS_ID : DAY_LABELS_EN;

  const closingSoonMin = 30;
  if (today && !today.closed) {
    const open = toMinutes(today.open);
    const close = toMinutes(today.close);
    if (open !== null && close !== null) {
      if (minutes >= open && minutes < close) {
        const remaining = close - minutes;
        if (remaining <= closingSoonMin) {
          return {
            state: "closed_soon",
            label: lang === "id" ? "Closing Soon" : "Closing Soon",
            sub: (lang === "id" ? "Closes at " : "Closes at ") + today.close,
          };
        }
        return {
          state: "open",
          label: lang === "id" ? "Buka Sekarang" : "Open Now",
          sub: (lang === "id" ? "Sampai " : "Until ") + today.close,
        };
      }
      if (minutes < open) {
        return {
          state: "closed",
          label: lang === "id" ? "Closed" : "Closed",
          sub: (lang === "id" ? "Buka pukul " : "Opens at ") + today.open,
        };
      }
    }
  }

  // Look ahead for next open day (max 7)
  const idxToday = DAY_KEYS.indexOf(dayKey);
  for (let i = 1; i <= 7; i++) {
    const k = DAY_KEYS[(idxToday + i) % 7];
    const d = hours.days[k];
    if (d && !d.closed && d.open) {
      return {
        state: "closed",
        label: lang === "id" ? "Closed" : "Closed",
        sub: `${lang === "id" ? "Buka " : "Opens "}${labels[k]} · ${d.open}`,
      };
    }
  }
  return {
    state: "closed",
    label: lang === "id" ? "Closed" : "Closed",
    sub: lang === "id" ? "Hubungi WhatsApp" : "Contact via WhatsApp",
  };
}

/** Tone tokens per state. `variant="light"` for white backgrounds, `"dark"` for dark surfaces. */
function styles(state, variant) {
  const dark = variant === "dark";
  if (state === "open") {
    return {
      dot: "bg-emerald-500",
      pulse: "bg-emerald-500/70",
      text: dark ? "text-emerald-300" : "text-emerald-800",
      bg:   dark ? "bg-emerald-500/10" : "bg-emerald-50",
      border: dark ? "border-emerald-500/30" : "border-emerald-200",
    };
  }
  if (state === "closed_soon") {
    return {
      dot: "bg-amber-500",
      pulse: "bg-amber-500/70",
      text: dark ? "text-amber-300" : "text-amber-800",
      bg:   dark ? "bg-amber-500/10" : "bg-amber-50",
      border: dark ? "border-amber-500/30" : "border-amber-200",
    };
  }
  return {
    dot: "bg-zinc-400",
    pulse: "bg-zinc-400/50",
    text: dark ? "text-zinc-300" : "text-zinc-700",
    bg:   dark ? "bg-zinc-500/10" : "bg-zinc-100",
    border: dark ? "border-zinc-500/30" : "border-zinc-200",
  };
}

/**
 * Live business-status pill. Auto-refreshes every 60 seconds.
 *
 * Props:
 *   size:    "sm" | "md" | "lg"     (default "sm")
 *   variant: "light" | "dark"        (default "light")
 *   showSub: boolean                  (default false — pill mode)
 *   className:string
 */
export default function BusinessStatus({
  size = "sm",
  variant = "light",
  showSub = false,
  className = "",
}) {
  const { i18n } = useTranslation();
  const [tick, setTick] = useState(0);
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 60_000,
  });

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const status = useMemo(() => {
    // Recompute on tick to advance the "closes in X" state every minute
    void tick;
    return computeStatus(settings?.opening_hours, i18n.language || "en");
  }, [settings?.opening_hours, i18n.language, tick]);

  if (!status) return null;
  const s = styles(status.state, variant);

  const sizing = {
    sm: { pill: "px-2.5 py-1 text-[10.5px] gap-1.5", dot: "w-1.5 h-1.5", pulse: "w-3 h-3" },
    md: { pill: "px-3 py-1.5 text-xs gap-2", dot: "w-2 h-2", pulse: "w-3.5 h-3.5" },
    lg: { pill: "px-4 py-2 text-sm gap-2.5", dot: "w-2.5 h-2.5", pulse: "w-4 h-4" },
  }[size];

  return (
    <div
      className={`inline-flex items-center rounded-full border font-semibold uppercase tracking-[0.16em] whitespace-nowrap ${s.bg} ${s.border} ${s.text} ${sizing.pill} ${className}`}
      data-testid="business-status"
      data-state={status.state}
      title={status.sub}
    >
      <span className="relative inline-flex items-center justify-center shrink-0">
        {status.state === "open" && (
          <span className={`absolute inline-flex ${sizing.pulse} rounded-full ${s.pulse} animate-ping opacity-75`} />
        )}
        <span className={`relative inline-flex ${sizing.dot} rounded-full ${s.dot}`} />
      </span>
      <span>{status.label}</span>
      {showSub && <span className="opacity-70 normal-case tracking-normal font-medium">· {status.sub}</span>}
    </div>
  );
}

/** Full weekly schedule table — used inside About > Visit Us block. */
export function OpeningHoursTable({ variant = "light" }) {
  const { t, i18n } = useTranslation();
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
    staleTime: 60_000,
  });
  const hours = settings?.opening_hours;
  if (!hours?.days) return null;
  const dark = variant === "dark";
  const labels = i18n.language === "id" ? DAY_LABELS_ID : DAY_LABELS_EN;
  const { dayKey } = nowInTimezone(hours.timezone);

  const rowOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return (
    <div
      className={`rounded-2xl border p-4 ${dark ? "border-zinc-800 bg-zinc-900/50" : "border-zinc-200 bg-white"}`}
      data-testid="opening-hours-table"
    >
      <div className="flex items-center gap-2 mb-3">
        <Clock size={14} className={dark ? "text-[#d4a017]" : "text-[#b8860b]"} />
        <div className={`text-[10.5px] uppercase tracking-[0.22em] font-semibold ${dark ? "text-zinc-400" : "text-zinc-600"}`}>
          {t("about.opening_hours", "Opening Hours")}
        </div>
        <div className="ml-auto">
          <BusinessStatus size="sm" variant={variant} />
        </div>
      </div>
      <div className="divide-y divide-current/10">
        {rowOrder.map((k) => {
          const d = hours.days[k];
          const isToday = k === dayKey;
          const closed = !d || d.closed;
          return (
            <div
              key={k}
              className={`flex items-center justify-between py-2 text-[13.5px] ${isToday ? "font-semibold" : ""} ${dark ? "text-zinc-200" : "text-zinc-900"}`}
              data-testid={`hours-row-${k}`}
            >
              <div className="flex items-center gap-2">
                {isToday && (
                  <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wider ${dark ? "bg-[#d4a017] text-zinc-950" : "bg-zinc-950 text-white"}`}>
                    {i18n.language === "id" ? "Hari ini" : "Today"}
                  </span>
                )}
                <span>{labels[k]}</span>
              </div>
              <div className={closed ? "text-red-500" : ""}>
                {closed ? (i18n.language === "id" ? "Closed" : "Closed") : `${d.open} – ${d.close}`}
              </div>
            </div>
          );
        })}
      </div>
      <div className={`mt-3 text-[10.5px] ${dark ? "text-zinc-500" : "text-zinc-500"}`}>
        {i18n.language === "id" ? "Zona waktu" : "Timezone"}: {hours.timezone || "Asia/Brunei"}
      </div>
    </div>
  );
}
